import { buildRecordDiscussionKey, emptyRecordDiscussionSummary } from "common/recordDiscussions";
import { CREATE_ANNOTATION_SUCCESS, DELETE_ANNOTATION_SUCCESS, UPDATE_ANNOTATION_SUCCESS } from "../annotations/actions";
import { ADD_COMMENT_SUCCESS, DELETE_COMMENT_SUCCESS, UPDATE_COMMENT_SUCCESS } from "../comments/actions";
import {
    GET_RECORD_DISCUSSION_SUMMARY_FAILURE,
    GET_RECORD_DISCUSSION_SUMMARY_REQUEST,
    GET_RECORD_DISCUSSION_SUMMARY_SUCCESS,
    RECORD_DISCUSSION_SUMMARY_FETCH_STARTED,
    REFRESH_RECORD_DISCUSSION_SUMMARY_REQUEST,
} from "./actions";

export const RECORD_DISCUSSION_PREVIEW_LIMIT = 3;

const initialState = {
    summaryByKey: {},
    pendingKeys: {},
    getRecordDiscussionSummaryResult: {},
};

const withPendingKeys = (pendingKeys, keys, value) => {
    const next = { ...pendingKeys };
    keys.forEach((key) => {
        if (value) {
            next[key] = true;
        } else {
            delete next[key];
        }
    });
    return next;
};

const findKeysForRecordId = (summaryByKey, recordId) => {
    if (!recordId) return [];
    const suffix = `:${recordId}`;
    return Object.keys(summaryByKey).filter((key) => key.endsWith(suffix));
};

const applyToKeys = (state, keys, updater) => {
    const touched = keys.filter((key) => !!state.summaryByKey[key]);
    if (touched.length === 0) {
        return state;
    }

    const summaryByKey = { ...state.summaryByKey };
    touched.forEach((key) => {
        summaryByKey[key] = updater(summaryByKey[key]);
    });

    return { ...state, summaryByKey };
};

const prependPreview = (preview, entry) => [entry, ...(preview || [])].slice(0, RECORD_DISCUSSION_PREVIEW_LIMIT);

const replaceInPreview = (preview, entry, idField) => (preview || []).map((item) => (item[idField] === entry[idField] ? { ...item, ...entry } : item));

export default function reducer(state = initialState, action) {
    switch (action.type) {
        case GET_RECORD_DISCUSSION_SUMMARY_REQUEST:
        case REFRESH_RECORD_DISCUSSION_SUMMARY_REQUEST:
            return state;

        case RECORD_DISCUSSION_SUMMARY_FETCH_STARTED: {
            const { recordType, recordIds } = action.payload;
            const keys = (recordIds || []).map((recordId) => buildRecordDiscussionKey(recordType, recordId));
            return {
                ...state,
                pendingKeys: withPendingKeys(state.pendingKeys, keys, true),
                getRecordDiscussionSummaryResult: { loading: true },
            };
        }

        case GET_RECORD_DISCUSSION_SUMMARY_SUCCESS: {
            const { recordType, recordIds, summaries } = action.payload;
            const keys = (recordIds || []).map((recordId) => buildRecordDiscussionKey(recordType, recordId));
            const summaryByKey = { ...state.summaryByKey };

            (recordIds || []).forEach((recordId) => {
                const key = buildRecordDiscussionKey(recordType, recordId);
                summaryByKey[key] = { ...emptyRecordDiscussionSummary, ...((summaries && summaries[recordId]) || {}) };
            });

            return {
                ...state,
                summaryByKey,
                pendingKeys: withPendingKeys(state.pendingKeys, keys, false),
                getRecordDiscussionSummaryResult: { success: true },
            };
        }

        case GET_RECORD_DISCUSSION_SUMMARY_FAILURE: {
            const { recordType, recordIds, error } = action.payload;
            const keys = (recordIds || []).map((recordId) => buildRecordDiscussionKey(recordType, recordId));
            return {
                ...state,
                pendingKeys: withPendingKeys(state.pendingKeys, keys, false),
                getRecordDiscussionSummaryResult: { error },
            };
        }

        case ADD_COMMENT_SUCCESS: {
            const { recordId, comment } = action.payload;
            return applyToKeys(state, findKeysForRecordId(state.summaryByKey, recordId), (summary) => ({
                ...summary,
                commentsCount: (summary.commentsCount || 0) + 1,
                commentsPreview: prependPreview(summary.commentsPreview, comment),
            }));
        }

        case UPDATE_COMMENT_SUCCESS: {
            const { recordId, comment } = action.payload || {};
            if (!recordId || !comment) return state;
            return applyToKeys(state, findKeysForRecordId(state.summaryByKey, recordId), (summary) => ({
                ...summary,
                commentsPreview: replaceInPreview(summary.commentsPreview, comment, "commentId"),
            }));
        }

        case DELETE_COMMENT_SUCCESS: {
            const { recordId, commentId } = action.payload;
            return applyToKeys(state, findKeysForRecordId(state.summaryByKey, recordId), (summary) => ({
                ...summary,
                commentsCount: Math.max(0, (summary.commentsCount || 0) - 1),
                commentsPreview: (summary.commentsPreview || []).filter((item) => item.commentId !== commentId),
            }));
        }

        case CREATE_ANNOTATION_SUCCESS: {
            const { recordKey, annotation } = action.payload;
            const isHighPriority = String(annotation?.priority || "").toLowerCase() === "high";
            return applyToKeys(state, [recordKey], (summary) => ({
                ...summary,
                annotationsCount: (summary.annotationsCount || 0) + 1,
                unresolvedAnnotationsCount: (summary.unresolvedAnnotationsCount || 0) + 1,
                highPriorityOpenCount: (summary.highPriorityOpenCount || 0) + (isHighPriority ? 1 : 0),
                annotationsPreview: prependPreview(summary.annotationsPreview, annotation),
            }));
        }

        case UPDATE_ANNOTATION_SUCCESS: {
            const { recordKey, annotation } = action.payload;
            if (!annotation) return state;
            return applyToKeys(state, [recordKey], (summary) => ({
                ...summary,
                annotationsPreview: replaceInPreview(summary.annotationsPreview, annotation, "annotationId"),
            }));
        }

        case DELETE_ANNOTATION_SUCCESS: {
            const { recordKey, annotationId } = action.payload;
            return applyToKeys(state, [recordKey], (summary) => {
                const removed = (summary.annotationsPreview || []).find((item) => item.annotationId === annotationId);
                const wasOpen = removed ? removed.closed !== true : true;
                const wasHighPriority = removed ? String(removed.priority || "").toLowerCase() === "high" : false;

                return {
                    ...summary,
                    annotationsCount: Math.max(0, (summary.annotationsCount || 0) - (wasOpen ? 1 : 0)),
                    unresolvedAnnotationsCount: Math.max(0, (summary.unresolvedAnnotationsCount || 0) - (wasOpen ? 1 : 0)),
                    highPriorityOpenCount: Math.max(0, (summary.highPriorityOpenCount || 0) - (wasHighPriority ? 1 : 0)),
                    closedAnnotationsCount: Math.max(0, (summary.closedAnnotationsCount || 0) - (wasOpen ? 0 : 1)),
                    annotationsPreview: (summary.annotationsPreview || []).filter((item) => item.annotationId !== annotationId),
                };
            });
        }

        default:
            return state;
    }
}
